import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Shield,
  Activity,
  DollarSign,
  BadgeCheck,
  Headphones,
  Users,
  Wrench,
  Thermometer,
  Settings,
  Zap,
  Home as HomeIcon,
  Droplets,
  Sun,
  CheckCircle,
  PhoneCall,
  PhoneIncoming,
  Megaphone,
  Route,
  Building2,
  Wallet,
  Target,
  Eye,
  Handshake,
  Scale,
  TrendingUp,
  Lock,
  Quote,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.07 } }),
};

const inView = { variants: fadeUp, initial: "hidden", whileInView: "visible", viewport: { once: true, margin: "-40px" } };

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const STEPS = [
  { icon: Megaphone, title: "Publishers Drive Calls", desc: "Our vetted publishers generate inbound calls through Google Ads, Meta, SEO, GMB, and other compliant traffic sources." },
  { icon: Route, title: "Calls Are Tracked & Routed", desc: "Every call runs through Ringba in real time — filtered by geo, hours, and call type, then routed to the right buyer instantly." },
  { icon: Building2, title: "Buyers Receive Live Customers", desc: "Advertisers connect with high-intent callers who are ready to book — and pay only for qualified, connected calls." },
  { icon: Wallet, title: "Publishers Get Paid", desc: "Calls that pass the 120-second post-IVR buffer are paid out on Weekly Net 7 or Monthly Net 10 terms — on time, every cycle." },
];

const MVV = [
  { icon: Target, title: "Our Mission", desc: "To connect advertisers with real, ready-to-buy customers over the phone — and reward the publishers who deliver them with fair payouts and on-time payments." },
  { icon: Eye, title: "Our Vision", desc: "To be the most trusted Pay-Per-Call network in the United States, known for call quality, transparency, and long-term partnerships on both sides of every call." },
];

const VALUES = [
  { icon: Scale, title: "Transparency", desc: "Real-time tracking, clear reporting, and no hidden deductions." },
  { icon: BadgeCheck, title: "Quality First", desc: "Strict buffers and fraud screening protect every campaign." },
  { icon: Handshake, title: "Partnership", desc: "We grow when our publishers and buyers grow." },
  { icon: Lock, title: "Reliability", desc: "A consistent payment history our partners can count on." },
];

const VERTICALS = [
  { icon: Wrench, name: "Plumbing" },
  { icon: Thermometer, name: "HVAC" },
  { icon: Settings, name: "Appliance Repair" },
  { icon: Zap, name: "Electrician" },
  { icon: HomeIcon, name: "Roofing" },
  { icon: Droplets, name: "Water Damage" },
  { icon: Shield, name: "Home Security" },
  { icon: Sun, name: "Solar" },
];

const PUBLISHER_POINTS = [
  "Competitive payouts — up to $515 per qualified call",
  "Weekly Net 7 and Monthly Net 10 payment terms",
  "Real-time Ringba tracking on every campaign",
  "Access to 100+ verticals and premium exclusive offers",
  "Dedicated publisher support, 7 days a week",
];

const BUYER_POINTS = [
  "High-intent inbound calls — customers call you",
  "Geo-targeted traffic down to state and zip level",
  "Flexible filters: buffer time, call type, hours, IVR",
  "Real-time call tracking and recording via Ringba",
  "Pay only for real, connected calls — never for clicks",
];

const WHY_CARDS = [
  { icon: Shield, title: "US-Registered LLC", desc: "A Wyoming-registered company operating under US business law — real contracts, real invoices, real accountability." },
  { icon: Activity, title: "Ringba-Powered Tracking", desc: "Every call is tracked, recorded, and reported in real time through Ringba — the industry's gold standard." },
  { icon: DollarSign, title: "Proven Payment History", desc: "Weekly Net 7 and Monthly Net 10 payouts delivered on schedule — a track record our publishers rely on." },
  { icon: BadgeCheck, title: "Strict Call Quality Standards", desc: "120-second post-IVR buffers, fraud screening, and call review keep quality high for both sides." },
  { icon: Headphones, title: "Dedicated Account Managers", desc: "A named contact who knows your campaigns, answers within hours, and helps you scale." },
  { icon: Users, title: "1,100+ Publisher Network", desc: "Vetted publishers running Google, Meta, SEO, and GMB traffic — real capacity ready when you are." },
];

const NUMBERS = [
  { value: "100+", label: "Active Verticals" },
  { value: "1,100+", label: "Publishers" },
  { value: "Weekly", label: "& Monthly Payouts" },
  { value: "120 sec", label: "Post-IVR Buffer" },
  { value: "Since 2016", label: "US-Registered" },
];

const Eyebrow = ({ children }) => (
  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-400">{children}</p>
);

const SectionTitle = ({ eyebrow, title, sub }) => (
  <div className="max-w-3xl">
    <Eyebrow>{eyebrow}</Eyebrow>
    <h2 className="mt-3 font-['Poppins'] text-[26px] leading-tight sm:text-4xl font-bold tracking-tight">{title}</h2>
    {sub && <p className="mt-3 sm:mt-4 text-sm sm:text-base text-slate-400 leading-relaxed">{sub}</p>}
  </div>
);

const IconBox = ({ icon: Icon, size = "w-11 h-11" }) => (
  <div className={`${size} rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-md shadow-purple-600/25 flex-shrink-0`}>
    <Icon className="w-5 h-5 text-white" strokeWidth={2.25} />
  </div>
);

const GlassCard = ({ children, i = 0, testId, className = "p-5 sm:p-6" }) => (
  <motion.div
    {...inView}
    custom={i}
    data-testid={testId}
    className={`rounded-2xl border border-slate-700/60 bg-slate-800/35 backdrop-blur-xl hover:border-blue-500/50 hover:-translate-y-1 transition-[transform,border-color] duration-300 ${className}`}
  >
    {children}
  </motion.div>
);

const WhoWeAre = () => (
  <section className="pt-28 pb-12 sm:pt-36 sm:pb-20 lg:pt-40 lg:pb-24 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-950" data-testid="about-who">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-6 sm:gap-10 lg:gap-12 text-left">
      <div className="lg:col-span-2">
        <SectionTitle eyebrow="Who We Are" title="A Pay-Per-Call Network Built on Real Calls, Not Clicks" />
      </div>
      <div className="lg:col-span-3 space-y-4 sm:space-y-5 text-[15px] sm:text-base text-slate-400 leading-relaxed">
        <p>
          Digital Master Media LLC began its digital marketing journey in 2014 and was established in the United States in 2016 with one clear purpose — to build a performance marketing network where publishers and advertisers grow together, reliably and transparently.
        </p>
        <p>
          Today we operate a Pay-Per-Call network spanning 100+ verticals and more than 1,100 active publishers. Buyers pay only for real, connected calls — no wasted ad spend on clicks that never convert. Every call is tracked through Ringba with post-IVR buffering, so quality is measured before a single dollar moves.
        </p>
        <p>
          For publishers, we deliver premium offers, competitive payouts, and a proven history of weekly and monthly payments. For advertisers, we deliver a vetted network of traffic partners sending customers who pick up the phone ready to buy. One network, built so both sides win.
        </p>
      </div>
    </div>
  </section>
);

const FounderPhoto = () => {
  const [failed, setFailed] = React.useState(false);
  if (failed) {
    return (
      <div className="w-full aspect-[4/5] rounded-3xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center" data-testid="founder-photo-placeholder">
        <span className="font-['Poppins'] text-7xl font-bold text-white/90">AA</span>
      </div>
    );
  }
  return <img src="/founder.jpg?v=3" alt="Abid Ali, Founder & CEO" onError={() => setFailed(true)} className="w-full aspect-[4/5] object-cover object-top rounded-3xl" data-testid="founder-photo" />;
};

const FounderNote = () => (
  <section className="py-12 sm:py-20 lg:py-24 border-t border-slate-800/70 bg-slate-900/40" data-testid="about-founder">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-10 lg:gap-14 items-center text-left">
      <motion.div {...inView} className="md:col-span-2 relative w-full max-w-[220px] sm:max-w-[280px] md:max-w-sm mx-auto md:mx-0">
        <div className="absolute -inset-4 bg-gradient-to-br from-blue-600/25 to-purple-600/20 rounded-3xl blur-2xl -z-10" />
        <div className="rounded-3xl border border-blue-500/30 p-2 bg-slate-800/40">
          <FounderPhoto />
        </div>
      </motion.div>
      <motion.div {...inView} custom={1} className="md:col-span-3">
        <Eyebrow>A Note From Our Founder</Eyebrow>
        <Quote className="mt-4 sm:mt-5 w-8 h-8 sm:w-10 sm:h-10 text-blue-500/60" />
        <div className="mt-4 space-y-4 text-slate-300 text-base sm:text-lg leading-relaxed" data-testid="founder-note">
          <p>
            I started in digital marketing in 2014 with a simple belief — the best partnerships are built on trust and results. In 2016, that belief became Digital Master Media LLC.
          </p>
          <p>
            From day one, our promise has been the same: deliver real, high-intent calls to our buyers and pay our publishers on time, every single cycle. Today, more than 1,100 publishers and advertisers across 100+ verticals rely on that promise.
          </p>
          <p>
            Every partner who joins our network is a relationship we take personally. Thank you for trusting us with your traffic and your growth.
          </p>
        </div>
        <div className="mt-8 border-t border-slate-800 pt-6">
          <p className="font-['Poppins'] text-xl font-bold text-white" data-testid="founder-name">Abid Ali</p>
          <p className="text-sm text-blue-400 font-medium">Founder & CEO, Digital Master Media LLC</p>
        </div>
      </motion.div>
    </div>
  </section>
);

const HowItWorks = () => (
  <section className="py-12 sm:py-20 lg:py-24 border-t border-slate-800/70 bg-slate-900/40" data-testid="about-how">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
      <SectionTitle eyebrow="How the Network Works" title="From First Ring to Final Payout" sub="A simple, transparent flow that keeps call quality high for buyers and payments reliable for publishers." />
      <div className="mt-8 sm:mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {STEPS.map((s, i) => (
          <GlassCard key={s.title} i={i} testId={`how-step-${i + 1}`} className="relative p-5 sm:p-6 flex sm:block gap-4">
            <span className="absolute top-4 right-5 sm:top-5 sm:right-6 font-['Poppins'] text-2xl sm:text-4xl font-extrabold text-slate-700/60">0{i + 1}</span>
            <IconBox icon={s.icon} />
            <div className="pr-8 sm:pr-0">
              <h3 className="sm:mt-5 font-semibold text-[15px]">{s.title}</h3>
              <p className="mt-1.5 sm:mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
            </div>
          </GlassCard>
        ))}
      </div>
    </div>
  </section>
);

const MissionVision = () => (
  <section className="py-12 sm:py-20 lg:py-24 border-t border-slate-800/70" data-testid="about-mission">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
      <SectionTitle eyebrow="Mission, Vision & Values" title="What Drives Digital Master Media" />
      <div className="mt-8 sm:mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {MVV.map((m, i) => (
          <GlassCard key={m.title} i={i} testId={`mvv-${slug(m.title)}`} className="p-6 sm:p-8 border-blue-500/30">
            <IconBox icon={m.icon} size="w-12 h-12" />
            <h3 className="mt-4 sm:mt-5 font-['Poppins'] text-xl sm:text-2xl font-bold">{m.title}</h3>
            <p className="mt-2 sm:mt-3 text-[15px] sm:text-base text-slate-400 leading-relaxed">{m.desc}</p>
          </GlassCard>
        ))}
      </div>
      <div className="mt-4 sm:mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        {VALUES.map((v, i) => (
          <GlassCard key={v.title} i={i} testId={`value-${slug(v.title)}`} className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-3">
              <IconBox icon={v.icon} size="w-9 h-9 sm:w-10 sm:h-10" />
              <h4 className="font-semibold text-sm sm:text-base">{v.title}</h4>
            </div>
            <p className="mt-2 sm:mt-3 text-xs sm:text-sm text-slate-400 leading-relaxed">{v.desc}</p>
          </GlassCard>
        ))}
      </div>
    </div>
  </section>
);

const Verticals = () => (
  <section className="py-12 sm:py-20 lg:py-24 border-t border-slate-800/70 bg-slate-900/40" data-testid="about-verticals">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
      <SectionTitle eyebrow="100+ Verticals" title="Campaigns Across Every Major Pay-Per-Call Category" sub="Our network runs offers in more than 100 verticals. Here are some of our top-performing home service campaigns." />
      <div className="mt-8 sm:mt-12 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        {VERTICALS.map((v, i) => (
          <GlassCard key={v.name} i={i} testId={`vertical-${slug(v.name)}`} className="p-3.5 sm:p-5 flex items-center gap-2.5 sm:gap-3">
            <IconBox icon={v.icon} size="w-9 h-9 sm:w-10 sm:h-10" />
            <span className="font-semibold text-[13px] sm:text-sm leading-tight">{v.name}</span>
          </GlassCard>
        ))}
      </div>
      <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 rounded-2xl border border-blue-500/30 bg-blue-500/5 px-5 py-4 sm:px-6 sm:py-5">
        <p className="text-slate-300 text-sm"><span className="font-semibold text-white">+ 90 more verticals</span> available to approved publishers and buyers.</p>
        <Link to="/offers" data-testid="about-verticals-offers-link" className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-semibold text-sm">
          Browse PPC Offers <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </section>
);

const SideCard = ({ testId, img, icon: Icon, title, intro, points, to, cta, accent }) => (
  <motion.div {...inView} className={`rounded-3xl border ${accent} bg-slate-800/40 backdrop-blur-xl overflow-hidden`} data-testid={testId}>
    <div className="relative h-44 sm:h-60 overflow-hidden">
      <img src={img} alt={title} className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
      <div className="absolute bottom-4 left-5 sm:left-6 flex items-center gap-2.5">
        <IconBox icon={Icon} size="w-10 h-10" />
        <h3 className="font-['Poppins'] text-xl sm:text-2xl font-bold text-white">{title}</h3>
      </div>
    </div>
    <div className="p-5 sm:p-8">
      <p className="text-slate-400 text-sm leading-relaxed mb-5">{intro}</p>
      <ul className="space-y-3">
        {points.map((p) => (
          <li key={p} className="flex items-start gap-3 text-sm text-slate-300">
            <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
            {p}
          </li>
        ))}
      </ul>
      <Link to={to} data-testid={`${testId}-cta`} className="mt-6 sm:mt-7 w-full sm:w-auto justify-center inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-semibold px-7 py-3 rounded-xl shadow-lg shadow-blue-600/25 hover:-translate-y-0.5 transition-transform text-sm">
        {cta} <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </motion.div>
);

const Sides = () => (
  <section className="py-12 sm:py-20 lg:py-24 border-t border-slate-800/70" data-testid="about-sides">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8">
      <SideCard testId="for-publishers" img="/about-publishers-ppc.jpg" icon={PhoneCall} title="For Publishers" accent="border-blue-500/30" intro="You know how to generate calls. We bring the premium offers, the tracking, and the payouts that make it worth your traffic." points={PUBLISHER_POINTS} to="/apply" cta="Apply as a Publisher" />
      <SideCard testId="for-buyers" img="/about-buyers-ppc.jpg" icon={PhoneIncoming} title="For Buyers" accent="border-purple-500/30" intro="Stop paying for clicks. Get customers who pick up the phone ready to book — with full control over targeting and quality." points={BUYER_POINTS} to="/buyer" cta="Apply as a Buyer" />
    </div>
  </section>
);

const WhyUs = () => (
  <section className="py-12 sm:py-20 lg:py-24 border-t border-slate-800/70 bg-slate-900/40" data-testid="about-why">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
      <SectionTitle eyebrow="Why Partner With Us" title="The Network Partners Trust With Their Traffic and Their Budgets" />
      <div className="mt-8 sm:mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {WHY_CARDS.map((c, i) => (
          <GlassCard key={c.title} i={i} testId={`why-card-${slug(c.title)}`} className="p-5 sm:p-6 flex sm:block gap-4">
            <IconBox icon={c.icon} />
            <div>
              <h3 className="sm:mt-5 font-semibold text-[15px]">{c.title}</h3>
              <p className="mt-1.5 sm:mt-2 text-sm text-slate-400 leading-relaxed">{c.desc}</p>
            </div>
          </GlassCard>
        ))}
      </div>
    </div>
  </section>
);

const Numbers = () => (
  <section className="py-14 sm:py-20 border-t border-slate-800/70" data-testid="about-numbers">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-5 gap-x-4 gap-y-8 text-center">
      {NUMBERS.map((n, i) => (
        <motion.div key={n.label} {...inView} custom={i} data-testid={`number-${slug(n.label)}`} className={i === NUMBERS.length - 1 ? "col-span-2 md:col-span-1" : ""}>
          <p className="font-['Poppins'] text-2xl sm:text-3xl lg:text-4xl font-extrabold gradient-text">{n.value}</p>
          <p className="mt-2 text-slate-400 text-[11px] uppercase tracking-wider font-semibold">{n.label}</p>
        </motion.div>
      ))}
    </div>
  </section>
);

const FinalCta = () => (
  <section className="py-14 sm:py-24 lg:py-28 border-t border-slate-800/70 relative overflow-hidden" data-testid="about-cta">
    <div className="absolute inset-0 bg-gradient-to-br from-blue-950/50 via-slate-950 to-purple-950/30" />
    <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <TrendingUp className="w-10 h-10 text-blue-400 mx-auto mb-5" />
      <h2 className="font-['Poppins'] text-[26px] leading-tight sm:text-4xl lg:text-5xl font-bold tracking-tight">Ready to Grow With Digital Master Media?</h2>
      <p className="mt-4 sm:mt-5 text-[#e2e8f0] text-base sm:text-lg max-w-xl mx-auto">
        Whether you generate calls or need them, our team will show you what performance-based growth looks like.
      </p>
      <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
        <Link to="/contact" data-testid="about-cta-button" className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-bold px-10 py-4 rounded-xl shadow-lg shadow-purple-600/30 hover:-translate-y-0.5 transition-transform">
          Contact Our Team <ArrowRight className="w-5 h-5" />
        </Link>
        <Link to="/offers" data-testid="about-cta-offers" className="inline-flex items-center justify-center gap-2 border-2 border-slate-600 text-slate-200 hover:border-blue-400 hover:text-white font-semibold px-10 py-4 rounded-xl transition-colors">
          Browse PPC Offers
        </Link>
      </div>
      <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-slate-400">
        {["US-Registered LLC", "Ringba-Powered Tracking", "Proven Payment History"].map((t) => (
          <span key={t} className="inline-flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-400" strokeWidth={2.5} />
            {t}
          </span>
        ))}
      </div>
    </div>
  </section>
);

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-['Inter',-apple-system,sans-serif] antialiased overflow-x-hidden" data-testid="about-page">
      <Header />
      <WhoWeAre />
      <FounderNote />
      <HowItWorks />
      <MissionVision />
      <Verticals />
      <Sides />
      <WhyUs />
      <Numbers />
      <FinalCta />
      <Footer />
    </div>
  );
}
